
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ServiceUpgradeInjectorService } from '@shims';
import { Notifications, PhotosConstants, VisitDetailPhotos } from '@types-lib';
import { SnackNotificationsDialogService } from '@ui-lib/services';
import { ENVIRONMENT, PIMSEnvironment } from 'environments';
import { EMPTY } from 'rxjs';
import { initialiseTask } from '../../../../../../../../types/photo';
import { SecureMessageDialogComponent } from '../../components/secure-message/secure-message-dialog.component';
import { TaskPhotosComponent } from '../task-photos/task-photos.component';
import { IdPhotosStore } from '@id-photos-store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'id-photos',
  imports: [TaskPhotosComponent],
  providers: [IdPhotosStore],
  templateUrl: './id-photos.component.html',
  styleUrl: './id-photos.component.css',
  schemas: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IdPhotosComponent implements OnInit, OnDestroy {
  private env = inject<PIMSEnvironment>(ENVIRONMENT);
  private readonly destroyRef = inject(DestroyRef);
  store = inject(IdPhotosStore);
  ServiceUpgradeInjectorService = inject(ServiceUpgradeInjectorService);
  dialog = inject(MatDialog);
  snackBar = inject(SnackNotificationsDialogService);

  @Input() visit: any = {};

  environment = this.env.constants.ENV;
  cannotUpdatePhoto = computed(() => this.store.isUpdating());
  task: VisitDetailPhotos.Task = initialiseTask();
  errorMessage = signal<string | null>(null);
  photos = this.store.processedPhotos;
  additionalPhotos = this.store.processedAdditionalPhotos;
  diagnosis = this.store.diagnosis;
  additionalDiagnosis = this.store.additionalDiagnosis;
  photoTypes = this.store.photoTypes;
  remainingRequests = computed(() => this.store.count()?.remaining_request_pair);
  cannotRequest = computed(() => {
    const count = this.store.count();
    const additionalPhotos = this.store.processedAdditionalPhotos();
    return !!(count?.id_and_selfie_unapproved || additionalPhotos.length);
  });
  cannotRequestMessage = this.store.cannotRequestMessage;
  //TODO: Use when we want to allow more request
  // For now default of false
  canRequestMore = false;
  //Constants
  ADDITIONAL_ID_ID = PhotosConstants.PhotoTypes.AdditionalId.id;
  ADDITIONAL_SELFIE_ID = PhotosConstants.PhotoTypes.AdditionalSelfie.id;
  ERROR_MESSAGES: VisitDetailPhotos.ErrorMessages = {
    400: Notifications.orders.locked,
    403: Notifications.orders.photos.selfieUploadedSeparately,
  };

  ngOnInit() {
    this.store.setVisitId(this.visit.id);
    this.store
      .onPhotoTaskSuccess()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ approved, additional }) => {
        this.ServiceUpgradeInjectorService.getAngularJsRootScope().$broadcast('photos:updated', approved);
        this.ServiceUpgradeInjectorService.getAngularJsRootScope().$broadcast('visit:refresh');
      });

    this.store
      .onPhotoTaskError()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ error, approved, additional }) => {
        const errorMessage =
          this.ERROR_MESSAGES[error.status as keyof VisitDetailPhotos.ErrorMessages] ||
          (error.data.message && error.data.message.summary) ||
          Notifications.orders.photos.failed;
        this.errorMessage.set(errorMessage);
        this.snackBar.openWarningMessage(errorMessage);
        return EMPTY;
      });
    this.store
      .onRequestAdditionalPhotoError()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ error }) => {
        const errorMessage = error?.message?.summary || 'Error sending request for additional ID Photos.';
        this.snackBar.openWarningMessage(errorMessage);
        return EMPTY;
      });
    this.store
      .onRequestAdditionalPhotoSuccess()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.snackBar.openSuccessMessage('Request for additional ID Photos sent.');
        return EMPTY;
      });
    this.store.startResourcePolling({
      resources: [
        { reload: this.store.photosResource, condition: this.store.hasUnapprovedPhotos },
        {
          reload: this.store.additionalPhotosResource,
          condition: this.store.hasUnapprovedAdditionalPhotos,
        },
      ],
      stopCondition: () => this.store.hasUnapprovedPhotos() && this.store.hasUnapprovedAdditionalPhotos(),
    });
  }

  reload() {
    this.store.refreshPhotos();
  }

  approve(value: VisitDetailPhotos.PropsPayload) {
    const { payload } = value;
    let approvedId = 0;
    const reviews = payload.photos.map((photo: VisitDetailPhotos.Photo) => {
      approvedId = photo.id;
      return {
        file_id: photo.file.id,
        approved: true,
        id_type: photo.file.id_type,
        notes: photo.notes,
      };
    });
    const additional = [this.ADDITIONAL_ID_ID, this.ADDITIONAL_SELFIE_ID].includes(approvedId);
    this.store.updatePhotoTask({ payload: { reviews }, approved: true, additional });
  }

  reject(value: VisitDetailPhotos.PropsPayload) {
    const { payload } = value;
    let rejectedId = 0;
    const reviews = payload.photos
      .filter((photo: VisitDetailPhotos.Photo) => photo.rejected)
      .map((photo: VisitDetailPhotos.Photo) => {
        rejectedId = photo.id;
        return {
          file_id: photo.file.id,
          approved: false,
          id_type: photo.file.id_type,
          notes: photo.notes,
          rejection_note: photo.file.rejection_note,
        };
      });
    const additional = [this.ADDITIONAL_ID_ID, this.ADDITIONAL_SELFIE_ID].includes(rejectedId);
    this.store.updatePhotoTask({ payload: { reviews }, approved: false, additional });
  }

  openMessageDialog(): void {
    const dialogRef = this.dialog.open(SecureMessageDialogComponent, { disableClose: true });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: string) => {
        if (result) {
          const payload = {
            request_note: result,
          };
          this.requestAdditionalPhotos(this.visit.id, payload);
        }
      });
  }

  requestAdditionalPhotos(visitId: number, payload: VisitDetailPhotos.RequestAdditionalIdMessage) {
    this.store.requestAdditionalId({ payload });
  }
  ngOnDestroy() {
    if (this.store.isPolling()) {
      this.store.stopResourcePolling();
    }
  }
}
